import type { ApiError } from '@/lib/apiClient';
import type { ApiValidationIssue } from '@/types/frontend-types';

const DEFAULT_VALIDATION_FIELD_LABEL = 'Field';
const DEFAULT_VALIDATION_ISSUE_MESSAGE = 'Invalid value';

enum SessionMutationValidationErrorCode {
  Validation = 'VALIDATION',
}

type ValidationIssueProperty = 'field' | 'message' | 'path';
type ValidationIssuePropertyValidator = (value: unknown) => boolean;

interface ValidationIssuePropertyRule {
  property: ValidationIssueProperty;
  isValidValue: ValidationIssuePropertyValidator;
}

interface ValidateIssuePropertyInput {
  issue: Record<string, unknown>;
  rule: ValidationIssuePropertyRule;
}

function isUndefinedValue(value: unknown): boolean {
  return Object.is(value, undefined);
}

function isOptionalString(value: unknown): boolean {
  return isUndefinedValue(value) || typeof value === 'string';
}

function isOptionalPath(value: unknown): boolean {
  return isUndefinedValue(value) || Array.isArray(value);
}

const VALIDATION_ISSUE_PROPERTY_RULES: ValidationIssuePropertyRule[] = [
  {
    property: 'field',
    isValidValue: isOptionalString,
  },
  {
    property: 'message',
    isValidValue: isOptionalString,
  },
  {
    property: 'path',
    isValidValue: isOptionalPath,
  },
];

function isObjectLikeRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

function isValidIssueProperty({
  issue,
  rule,
}: ValidateIssuePropertyInput): boolean {
  return rule.isValidValue(issue[rule.property]);
}

function isValidationIssue(value: unknown): value is ApiValidationIssue {
  if (!isObjectLikeRecord(value)) {
    return false;
  }

  return VALIDATION_ISSUE_PROPERTY_RULES.every((rule) => (
    isValidIssueProperty({ issue: value, rule })
  ));
}

function humanizeFieldSegment(segment: string): string {
  const spaced = segment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();

  return spaced.length > 0
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : DEFAULT_VALIDATION_FIELD_LABEL;
}

function labelFromPath(path: Array<string | number>): string {
  if (path.length === 0) return DEFAULT_VALIDATION_FIELD_LABEL;

  return path
    .map((segment) => (
      typeof segment === 'number'
        ? String(segment + 1)
        : humanizeFieldSegment(segment)
    ))
    .join(' ');
}

function labelFromField(field: string | undefined): string {
  if (!field) return DEFAULT_VALIDATION_FIELD_LABEL;

  const bracketExpanded = field.replace(/\[(\d+)\]/g, (_match, index: string) => (
    ` ${Number(index) + 1} `
  ));

  return bracketExpanded
    .split('.')
    .map(humanizeFieldSegment)
    .join(' ');
}

function formatValidationIssue(issue: ApiValidationIssue): string {
  const label = Array.isArray(issue.path)
    ? labelFromPath(issue.path)
    : labelFromField(issue.field);
  const message = issue.message ?? DEFAULT_VALIDATION_ISSUE_MESSAGE;
  return `${label}: ${message}`;
}

function validationIssuesFrom(error: ApiError): ApiValidationIssue[] {
  const issues = error.details?.issues;
  return issues ? issues.filter(isValidationIssue) : [];
}

export function resolveSessionMutationValidationErrorBody(
  error: ApiError,
): string | null {
  if (error.code !== SessionMutationValidationErrorCode.Validation) {
    return null;
  }

  const issues = validationIssuesFrom(error);
  return issues.length > 0
    ? issues.map(formatValidationIssue).join('\n')
    : error.userMessage ?? null;
}
